import { Chip } from "@legal-500/legal500-design-system";
import { createElement } from "react";
import { z } from "zod";
import { regionCountries, type RegionNode } from "../../api/regions.service";
import type { HierarchicalItem, HierarchyManagerConfig } from "../../components/HierarchyManager";
import { regionNodeKey } from "../../hooks/useRegions";

/**
 * HierarchicalItem flavour for the WordPress region/country tree. We carry the
 * original `kind` and numeric `nodeId` so `customNodeLabel` can render the kind
 * chip and surface the underlying id (the tree `id` is the composite
 * `${kind}:${id}` key, since region- and country-id spaces collide).
 */
type RegionCountryItem = HierarchicalItem & {
  kind: RegionNode["kind"];
  nodeId: number;
};

/**
 * Map a flat `RegionNode` (from `api_region_country_v2`) to a `HierarchicalItem`.
 * The tree key MUST be the composite `${kind}:${id}` because region- and
 * country-ids share no namespace and can collide numerically. All name/slug
 * fields are null-safe.
 */
function mapNode(node: RegionNode): RegionCountryItem {
  return {
    id: regionNodeKey(node),
    parentId:
      node.parentId != null && node.parentKind
        ? regionNodeKey({ kind: node.parentKind, id: node.parentId })
        : null,
    name: node.name ?? "—",
    slug: node.slug ?? undefined,
    displayOrder: node.displayOrder,
    kind: node.kind,
    nodeId: node.id,
  };
}

/**
 * Read-only adapter over the existing `regionCountries.getTree()` service. The
 * mutating operations are never invoked (the manager is `readOnly`), so they
 * throw to make accidental wiring loud.
 */
const apiService = {
  getAll: async (): Promise<RegionCountryItem[]> => {
    const nodes = await regionCountries.getTree();
    return nodes.map(mapNode);
  },
  create: (): Promise<RegionCountryItem> => {
    throw new Error("read-only");
  },
  update: (): Promise<RegionCountryItem> => {
    throw new Error("read-only");
  },
  delete: (): Promise<void> => {
    throw new Error("read-only");
  },
  move: (): Promise<RegionCountryItem> => {
    throw new Error("read-only");
  },
};

export const regionCountriesConfig: HierarchyManagerConfig<RegionCountryItem> = {
  title: "WordPress Regions",
  singularName: "Region",
  pluralName: "Regions",

  apiService,

  // No form: the tree is read-only.
  formFields: [],
  validationSchema: z.object({}),

  enableSearch: true,
  enableDragAndDrop: false,
  enableBreadcrumbs: false,
  showSlug: true,
  treeMode: "tree",
  readOnly: true,

  // Show the node kind as a theme-aware chip alongside the numeric id + slug.
  customNodeLabel: (item: RegionCountryItem) =>
    createElement(
      "span",
      {
        style: {
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap",
        },
      },
      createElement(
        Chip,
        {
          size: "small",
          color: item.kind === "region" ? "primary" : "secondary",
        },
        item.kind,
      ),
      createElement("span", null, item.name ?? "—"),
      createElement(
        "span",
        { style: { opacity: 0.6, fontSize: "0.85em" } },
        `#${item.nodeId}${item.slug ? ` · ${item.slug}` : ""}`,
      ),
    ),
};
